import React, { useEffect, useState } from "react";
import { servingApi, type Lang, type Materials, type Tier } from "../../../api/serving";
import { fetchActivity, type ActivityInfo } from "../../../api/peContent";
import { peActivitiesEs } from "../../../data/peActivitiesEs";
import { peT } from "../../../i18n/peLesson";
import {
  ChecklistPanel,
  FamilyLetterPanel,
  MaterialDocPanel,
  PrintButton,
  PrintCopy,
} from "./shared/MaterialDocs";

/**
 * Teacher materials rail for the signed-in Curriculum chapter page (Run E3 B2, D11).
 *
 * A teacher can open the teacher guide, quick reference, read-aloud script,
 * observation checklist, family letter and the extension activity for a chapter
 * without assigning it first. The tier switch picks the "For Tier N" callout in
 * the guide / quick reference. Every material has a Print button and a clean
 * letter-size print view (print copy rendered by the panels, see materialsPrint.css).
 * Read-only: nothing is written.
 */

type RailTab = "guide" | "quickref" | "readaloud" | "checklist" | "family" | "activity";
const RAIL_TABS: RailTab[] = ["guide", "quickref", "readaloud", "checklist", "family", "activity"];
const TIERS: Tier[] = ["T1", "T2", "T3", "T4"];

function localizeActivity(a: ActivityInfo, lang: Lang): ActivityInfo {
  if (lang !== "es" || !a.title) return a;
  const es = peActivitiesEs[a.title.trim()];
  if (!es) return a;
  const sameDesc = (a.description ?? "").trim() === (es.description_en ?? "");
  return { title: es.title_es, description: sameDesc ? es.description_es : a.description };
}

const TeacherMaterialsRail: React.FC<{
  chapterId: string;
  chapterLabel: string;
  tier: Tier;
  onTierChange: (t: Tier) => void;
  lang: Lang;
}> = ({ chapterId, chapterLabel, tier, onTierChange, lang }) => {
  const t = peT(lang);
  const r = t.materialsRail;
  const [tab, setTab] = useState<RailTab>("guide");
  const [materials, setMaterials] = useState<Materials | null>(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activity, setActivity] = useState<ActivityInfo | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    servingApi
      .getMaterials({ chapterId, language: lang })
      .then((m) => { if (!cancelled) setMaterials(m); })
      .catch((e) => {
        console.warn("[TeacherMaterialsRail] materials load failed:", e);
        if (!cancelled) { setMaterials(null); setFailed(true); }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [chapterId, lang]);

  useEffect(() => {
    if (tab !== "activity" || activity) return;
    let cancelled = false;
    fetchActivity(chapterId)
      .then((a) => { if (!cancelled) setActivity(a); })
      .catch((e) => console.warn("[TeacherMaterialsRail] activity load failed:", e));
    return () => { cancelled = true; };
  }, [tab, chapterId, activity]);

  const printHeading = r.printed(chapterLabel, t.tier(tier), lang.toUpperCase());
  const shownActivity = activity ? localizeActivity(activity, lang) : null;

  return (
    <section
      className="border-t border-gray-200 bg-gray-50 p-4 md:p-6 pe-noprint"
      aria-label={r.title}
      data-testid="materials-rail"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900">{r.title}</h3>
          <p className="text-sm text-gray-600">{r.blurb}</p>
        </div>
        <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden bg-white" role="group" aria-label={r.tierSwitch}>
          {TIERS.map((tr) => (
            <button
              key={tr}
              type="button"
              onClick={() => onTierChange(tr)}
              aria-pressed={tier === tr}
              className={`px-3 py-2 text-sm font-semibold ${
                tier === tr ? "bg-indigo-600 text-white" : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              {t.tier(tr)}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[14rem_minmax(0,1fr)] gap-4 items-start">
        <ul className="flex lg:flex-col overflow-x-auto lg:overflow-visible rounded-lg border border-gray-200 bg-white" role="tablist" aria-label={r.title}>
          {RAIL_TABS.map((key) => (
            <li key={key} className="shrink-0">
              <button
                type="button"
                role="tab"
                aria-selected={tab === key}
                onClick={() => setTab(key)}
                className={`w-full text-left whitespace-nowrap px-4 py-2 text-sm font-medium border-b-2 lg:border-b-0 lg:border-l-4 transition-colors ${
                  tab === key
                    ? "bg-indigo-50 text-indigo-700 border-indigo-600"
                    : "border-transparent text-gray-600 hover:bg-gray-100"
                }`}
              >
                {t.tabs[key]}
              </button>
            </li>
          ))}
        </ul>

        <div className="min-w-0 rounded-lg border border-gray-200 bg-white p-4 md:p-5" role="tabpanel">
          {loading && tab !== "activity" ? (
            <p className="text-sm text-gray-500">{r.loading}</p>
          ) : failed && tab !== "activity" ? (
            <p className="text-sm text-red-600">{r.loadFailed}</p>
          ) : (
            <>
              {tab === "guide" && (
                <MaterialDocPanel doc={materials?.teacher_guide} empty={t.noDoc.guide} lang={lang} tier={tier} printHeading={printHeading} showPrintButton />
              )}
              {tab === "quickref" && (
                <MaterialDocPanel doc={materials?.quick_reference} empty={t.noDoc.quickref} lang={lang} tier={tier} printHeading={printHeading} showPrintButton />
              )}
              {tab === "readaloud" && (
                <MaterialDocPanel doc={materials?.read_aloud_script} empty={t.noDoc.readaloud} lang={lang} tier={tier} printHeading={printHeading} showPrintButton />
              )}
              {tab === "checklist" && (
                <ChecklistPanel checklist={materials?.observation_checklist} empty={t.noChecklist} lang={lang} printHeading={printHeading} showPrintButton />
              )}
              {tab === "family" && (
                <FamilyLetterPanel letter={materials?.family_letter} empty={t.noFamilyLetter} lang={lang} printHeading={printHeading} showPrintButton />
              )}
              {tab === "activity" && (
                <section>
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold text-gray-900">{r.activityTitle}</h3>
                    <PrintButton lang={lang} />
                  </div>
                  {shownActivity && (shownActivity.title || shownActivity.description) ? (
                    <>
                      <h4 className="text-base font-bold text-gray-900">{shownActivity.title}</h4>
                      {shownActivity.description && (
                        <p className="mt-2 text-sm text-gray-700">{shownActivity.description}</p>
                      )}
                      <PrintCopy heading={printHeading}>
                        <h1 className="pe-print-title">{shownActivity.title}</h1>
                        {shownActivity.description && <p>{shownActivity.description}</p>}
                      </PrintCopy>
                    </>
                  ) : (
                    <p className="text-sm text-gray-500">{r.noActivity}</p>
                  )}
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default TeacherMaterialsRail;
